import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown, BarChart3, Loader2, RefreshCw } from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import { inputCls, labelCls, pageTitleCls, pageSubtitleCls } from '../../components/ui';
import { BarsFaseCosecha } from './BarsFaseCosecha';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtCRC(n: number): string {
  if (!Number.isFinite(n) || n === 0) return '—';
  return '₡' + n.toLocaleString('es-CR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
function fmtN(n: number, dec = 2): string {
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('es-CR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}
function fmtInt(n: number): string {
  return Math.round(n).toLocaleString('es-CR');
}

// Por defecto: año 2025 completo hasta hoy
function fechaHoy(): string {
  return new Date().toISOString().slice(0, 10);
}

function scrollDetalle(ref: React.RefObject<HTMLDivElement>) {
  window.setTimeout(() => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, 40);
}

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface FilaReporte {
  lote_num: string;
  finca: string;
  area_total: number;       // ha (suma sobre cédulas aplicadas)
  plantas_total: number;
  n_aplicaciones: number;
  costo_total: number;
  costo_por_ha: number;
  costo_por_planta: number;
  // Desglose por tipo de aplicación (Control sanitario, Nutrición, etc.)
  // — usado por el tooltip del BarChart de costo/ha.
  desglose_tipo: { tipo: string; costo: number }[];
}

type SortKey = keyof FilaReporte;
type SortDir = 'asc' | 'desc';

interface ColDef {
  key: SortKey;
  label: string;
  align?: 'left' | 'right';
  num?: boolean;
}

const COLS: ColDef[] = [
  { key: 'lote_num',         label: 'Lote',          align: 'left' },
  { key: 'finca',            label: 'Finca',         align: 'left' },
  { key: 'area_total',       label: 'Área (ha)',     align: 'right', num: true },
  { key: 'plantas_total',    label: 'Plantas',       align: 'right', num: true },
  { key: 'n_aplicaciones',   label: 'Aplicaciones',  align: 'right', num: true },
  { key: 'costo_total',      label: 'Costo total',   align: 'right', num: true },
  { key: 'costo_por_ha',     label: 'Costo / ha',    align: 'right', num: true },
  { key: 'costo_por_planta', label: 'Costo / planta',align: 'right', num: true },
];

function SortIcon({ active, dir }: { active: boolean; dir: SortDir }) {
  if (!active) return <ChevronsUpDown size={10} style={{ opacity: 0.35 }} />;
  return dir === 'asc' ? <ChevronUp size={10} /> : <ChevronDown size={10} />;
}

// ─── Donut: costo por estadio ─────────────────────────────────────────────────

const ESTADIO_COLORS: Record<string, string> = {
  'Desarrollo PC': 'var(--tone-success-text)',
  'Post Forza':    'var(--tone-info-text)',
  'Desarrollo RC': 'var(--tone-warn-text)',
  'Semillero RC':  'var(--tone-purple-text)',
  'Semillero PC':  'var(--emp-accent-txt)',
  'Barbecho':      'var(--ink-faint)',
  'Sin estadio':   'var(--ink-muted)',
};
const FALLBACK_COLORS = [
  'var(--tone-info-text)',
  'var(--tone-warn-text)',
  'var(--tone-success-text)',
  'var(--tone-purple-text)',
  'var(--emp-accent-txt)',
  'var(--ink-muted)',
];

// Categorización agronómica del ciclo VFP → tipo de aplicación.
// 8 categorías para que el donut quede legible y "Otros" sea residual.
const TIPO_CICLO_COLORS: Record<string, string> = {
  'Control sanitario': 'var(--tone-warn-text)',
  'Nutrición':         'var(--tone-success-text)',
  'Herbicidas':        'var(--emp-accent-txt)',
  'Forzamiento':       'var(--tone-purple-text)',
  'Maduración':        'var(--tone-info-text)',
  'Derriba':           'var(--tone-warn-text)',
  'Preparación suelo': 'var(--tone-success-text)',
  'Otros':             'var(--ink-faint)',
};
function clasificarCiclo(ciclo: string): string {
  const s = ciclo.toLowerCase().trim();
  if (!s) return 'Otros';
  // Derriba (incluye typo "derriva") — operación post-cosecha relevante
  if (/derriba|derriva/.test(s)) return 'Derriba';
  // Preparación suelo: quema, cal, pre-siembra, deshija
  if (/\bquema\b|\bcal\b|pre[\s\-]?siembra|deshij/.test(s)) return 'Preparación suelo';
  // Forzamiento / hormonas reguladoras
  if (/forza|hormona/.test(s)) return 'Forzamiento';
  // Maduración (incluye typos: aduracion, maduraion)
  if (/madurac|aduracion|maduraion/.test(s)) return 'Maduración';
  // Herbicidas (antes que sanitario para no confundir con 'herbi' dentro de 'herbicida')
  // Incluye "HERB. ADICION", "HERB.ADICION"
  if (/herbi|herbicida|\bherb\b|\bherb\.?\s*adicion/.test(s)) return 'Herbicidas';
  // Nutrición
  if (/foliar|folia|granular|fertili/.test(s)) return 'Nutrición';
  // Control sanitario (incluye typo "fungicia")
  if (/plag|fungicida|fungicia|insecticida|bactericida|roedor|control de plagas|cura|sello|\benf\b/.test(s)) return 'Control sanitario';
  return 'Otros';
}

interface EstadioReporte {
  estadio: string;
  costo: number;
  area: number;
  n_aplicaciones: number;
  detalle: { ciclo: string; costo: number }[];
}

function DonutEstadio({ data }: { data: EstadioReporte[] }) {
  const [activo, setActivo] = useState<number | null>(null);
  const [fijado, setFijado] = useState<number | null>(null);
  const detalleRef = useRef<HTMLDivElement>(null);
  const total = data.reduce((s, d) => s + d.costo, 0);
  if (total === 0 || data.length === 0) return null;
  const indiceActivo = fijado ?? activo;

  const R = 80, cx = 100, cy = 100;
  let angle = -Math.PI / 2;
  const slices = data.map((d, i) => {
    const pct = d.costo / total;
    const startAngle = angle;
    angle += pct * 2 * Math.PI;
    const endAngle = angle;
    const x1 = cx + R * Math.cos(startAngle);
    const y1 = cy + R * Math.sin(startAngle);
    const x2 = cx + R * Math.cos(endAngle);
    const y2 = cy + R * Math.sin(endAngle);
    const largeArc = pct > 0.5 ? 1 : 0;
    const path = `M ${cx} ${cy} L ${x1} ${y1} A ${R} ${R} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    const color = ESTADIO_COLORS[d.estadio] || FALLBACK_COLORS[i % FALLBACK_COLORS.length];
    return { path, color, pct };
  });

  return (
    <div style={{
      padding: '14px 18px', borderRadius: '12px',
      background: 'var(--surface-raised)', border: '1px solid var(--line)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <BarChart3 size={15} style={{ color: 'var(--tone-success-text)' }} />
        <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)' }}>
          Costo total por estadio
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap', position: 'relative' }}>
        <svg viewBox="0 0 200 200" style={{ width: '160px', height: '160px', flexShrink: 0 }}>
          {slices.map((s, i) => (
            <path key={i} d={s.path} fill={s.color} stroke="var(--surface-base)" strokeWidth="1.5"
              style={{
                cursor: 'pointer',
                opacity: indiceActivo == null || indiceActivo === i ? 1 : 0.35,
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={() => { if (fijado == null) setActivo(i); }}
              onMouseLeave={() => { if (fijado == null) setActivo(null); }}
              onClick={() => {
                setFijado(curr => {
                  const next = curr === i ? null : i;
                  if (next != null) scrollDetalle(detalleRef);
                  return next;
                });
              }}
            />
          ))}
          <circle cx={cx} cy={cy} r={38} fill="var(--surface-base)" />
          <text x={cx} y={cy - 4} textAnchor="middle" fill="var(--ink)" fontSize="11" fontWeight="800">
            {fmtCRC(total)}
          </text>
          <text x={cx} y={cy + 9} textAnchor="middle" fill="var(--ink-faint)" fontSize="8">
            total
          </text>
        </svg>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '180px' }}>
          {data.map((d, i) => {
            const pct = (d.costo / total) * 100;
            const color = ESTADIO_COLORS[d.estadio] || FALLBACK_COLORS[i % FALLBACK_COLORS.length];
            const esActivo = indiceActivo === i;
            return (
              <div key={d.estadio}
                onMouseEnter={() => { if (fijado == null) setActivo(i); }}
                onMouseLeave={() => { if (fijado == null) setActivo(null); }}
                onClick={() => {
                  setFijado(curr => {
                    const next = curr === i ? null : i;
                    if (next != null) scrollDetalle(detalleRef);
                    return next;
                  });
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  cursor: 'pointer',
                  padding: '2px 4px',
                  margin: '-2px -4px',
                  borderRadius: 4,
                  background: esActivo ? 'var(--emp-accent-bg)' : 'transparent',
                  outline: fijado === i ? `1px solid ${color}66` : 'none',
                  opacity: indiceActivo == null || esActivo ? 1 : 0.5,
                  transition: 'opacity 0.15s, background 0.15s',
                }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: color, flexShrink: 0 }} />
                <div style={{ flex: 1, fontSize: '12px', color: 'var(--ink)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {d.estadio}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <span style={{ fontSize: '12px', fontWeight: 800, color }}>{pct.toFixed(1)}%</span>
                  <span style={{ fontSize: '10px', color: 'var(--ink-faint)', marginLeft: '6px', fontFamily: 'monospace' }}>
                    {fmtCRC(d.costo)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {indiceActivo != null && data[indiceActivo] && (() => {
          const sel = data[indiceActivo];
          const color = ESTADIO_COLORS[sel.estadio] || FALLBACK_COLORS[indiceActivo % FALLBACK_COLORS.length];
          const costoHa = sel.area > 0 ? sel.costo / sel.area : 0;
          return (
            <div style={{
              scrollMarginBlock: '18px',
              flexBasis: '100%',
              width: '100%', maxHeight: '260px', overflowY: 'auto',
              marginTop: '10px',
              padding: '12px 14px', borderRadius: '10px',
              background: 'var(--surface-overlay)',
              border: `1px solid ${color}55`,
              boxShadow: '0 0 0 1px var(--line-dim)',
            }} ref={detalleRef}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid var(--line)' }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: color }} />
                <span style={{ fontSize: '12px', fontWeight: 800, color: 'var(--ink)' }}>
                  {sel.estadio}
                </span>
                <span style={{ fontSize: '10px', color: color, marginLeft: 'auto', fontFamily: 'monospace', fontWeight: 800 }}>
                  {((sel.costo / total) * 100).toFixed(1)}%
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '10px', color: 'var(--ink-faint)', marginBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Costo total</span>
                  <span style={{ fontFamily: 'monospace', color: 'var(--ink)', fontWeight: 700 }}>{fmtCRC(sel.costo)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Costo/ha</span>
                  <span style={{ fontFamily: 'monospace', color, fontWeight: 700 }}>{fmtCRC(costoHa)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Aplicaciones Â· Ãrea</span>
                  <span style={{ fontFamily: 'monospace', color: 'var(--ink-muted)' }}>{sel.n_aplicaciones} Â· {sel.area.toFixed(2)} ha</span>
                </div>
              </div>
              <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--ink-faint)', marginBottom: '6px', letterSpacing: '0.05em' }}>
                {sel.detalle.length} ciclo{sel.detalle.length === 1 ? '' : 's'}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {sel.detalle.map(d => {
                  const pctDet = sel.costo > 0 ? (d.costo / sel.costo) * 100 : 0;
                  return (
                    <div key={d.ciclo} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
                      <span style={{ flex: 1, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {d.ciclo}
                      </span>
                      <span style={{ fontFamily: 'monospace', color: 'var(--ink-muted)', fontSize: '10px' }}>
                        {pctDet.toFixed(1)}%
                      </span>
                      <span style={{ fontFamily: 'monospace', color, fontWeight: 700, fontSize: '10px', minWidth: '70px', textAlign: 'right' }}>
                        {fmtCRC(d.costo)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ─── Donut: costo por ciclo (Top 9 + Otros) ───────────────────────────────────

function DonutCiclo({ data }: { data: { etiqueta: string; costo: number; detalle: { ciclo: string; costo: number }[] }[] }) {
  const [activo, setActivo] = useState<number | null>(null);
  const [fijado, setFijado] = useState<number | null>(null);
  const detalleRef = useRef<HTMLDivElement>(null);
  const total = data.reduce((s, d) => s + d.costo, 0);
  if (total === 0 || data.length === 0) return null;
  const indiceActivo = fijado ?? activo;

  const R = 80, cx = 100, cy = 100;
  let angle = -Math.PI / 2;
  const slices = data.map((d, i) => {
    const pct = d.costo / total;
    const startAngle = angle;
    angle += pct * 2 * Math.PI;
    const endAngle = angle;
    const x1 = cx + R * Math.cos(startAngle);
    const y1 = cy + R * Math.sin(startAngle);
    const x2 = cx + R * Math.cos(endAngle);
    const y2 = cy + R * Math.sin(endAngle);
    const largeArc = pct > 0.5 ? 1 : 0;
    const path = `M ${cx} ${cy} L ${x1} ${y1} A ${R} ${R} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    const color = TIPO_CICLO_COLORS[d.etiqueta] || FALLBACK_COLORS[i % FALLBACK_COLORS.length];
    return { path, color, pct };
  });

  return (
    <div style={{
      padding: '14px 18px', borderRadius: '12px',
      background: 'var(--surface-raised)', border: '1px solid var(--line)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <BarChart3 size={15} style={{ color: 'var(--tone-purple-text)' }} />
        <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)' }}>
          Costo total por tipo de aplicación
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap', position: 'relative' }}>
        <svg viewBox="0 0 200 200" style={{ width: '160px', height: '160px', flexShrink: 0 }}>
          {slices.map((s, i) => (
            <path key={i} d={s.path} fill={s.color} stroke="var(--surface-base)" strokeWidth="1.5"
              style={{
                cursor: 'pointer',
                opacity: indiceActivo == null || indiceActivo === i ? 1 : 0.35,
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={() => { if (fijado == null) setActivo(i); }}
              onMouseLeave={() => { if (fijado == null) setActivo(null); }}
              onClick={() => {
                setFijado(curr => {
                  const next = curr === i ? null : i;
                  if (next != null) scrollDetalle(detalleRef);
                  return next;
                });
              }}
            />
          ))}
          <circle cx={cx} cy={cy} r={38} fill="var(--surface-base)" />
          <text x={cx} y={cy - 4} textAnchor="middle" fill="var(--ink)" fontSize="11" fontWeight="800">
            {fmtCRC(total)}
          </text>
          <text x={cx} y={cy + 9} textAnchor="middle" fill="var(--ink-faint)" fontSize="8">
            total
          </text>
        </svg>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px', minWidth: '180px' }}>
          {data.map((d, i) => {
            const pct = (d.costo / total) * 100;
            const color = TIPO_CICLO_COLORS[d.etiqueta] || FALLBACK_COLORS[i % FALLBACK_COLORS.length];
            const esActivo = indiceActivo === i;
            return (
              <div key={d.etiqueta}
                onMouseEnter={() => { if (fijado == null) setActivo(i); }}
                onMouseLeave={() => { if (fijado == null) setActivo(null); }}
                onClick={() => {
                  setFijado(curr => {
                    const next = curr === i ? null : i;
                    if (next != null) scrollDetalle(detalleRef);
                    return next;
                  });
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  cursor: 'pointer',
                  padding: '2px 4px',
                  margin: '-2px -4px',
                  borderRadius: 4,
                  background: esActivo ? 'var(--emp-accent-bg)' : 'transparent',
                  outline: fijado === i ? `1px solid ${color}66` : 'none',
                  opacity: indiceActivo == null || esActivo ? 1 : 0.5,
                  transition: 'opacity 0.15s, background 0.15s',
                }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: color, flexShrink: 0 }} />
                <div style={{ flex: 1, fontSize: '12px', color: 'var(--ink)', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {d.etiqueta}
                  <span style={{ marginLeft: 4, fontSize: '10px', color: 'var(--ink-faint)', fontWeight: 500 }}>
                    ({d.detalle.length})
                  </span>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <span style={{ fontSize: '12px', fontWeight: 800, color }}>{pct.toFixed(1)}%</span>
                  <span style={{ fontSize: '10px', color: 'var(--ink-faint)', marginLeft: '6px', fontFamily: 'monospace' }}>
                    {fmtCRC(d.costo)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Panel de tooltip: detalle de ciclos del tipo activo */}
        {indiceActivo != null && data[indiceActivo] && (() => {
          const sel = data[indiceActivo];
          const color = TIPO_CICLO_COLORS[sel.etiqueta] || FALLBACK_COLORS[indiceActivo % FALLBACK_COLORS.length];
          return (
            <div style={{
              scrollMarginBlock: '18px',
              flexBasis: '100%',
              width: '100%', maxHeight: '260px', overflowY: 'auto',
              marginTop: '10px',
              padding: '12px 14px', borderRadius: '10px',
              background: 'var(--surface-overlay)',
              border: `1px solid ${color}55`,
              boxShadow: '0 0 0 1px var(--line-dim)',
            }} ref={detalleRef}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid var(--line)' }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: color }} />
                <span style={{ fontSize: '12px', fontWeight: 800, color: 'var(--ink)' }}>
                  {sel.etiqueta}
                </span>
                <span style={{ fontSize: '10px', color: 'var(--ink-faint)', marginLeft: 'auto', fontFamily: 'monospace' }}>
                  {fmtCRC(sel.costo)}
                </span>
              </div>
              <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--ink-faint)', marginBottom: '6px', letterSpacing: '0.05em' }}>
                {sel.detalle.length} ciclo{sel.detalle.length === 1 ? '' : 's'}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {sel.detalle.map(d => {
                  const pctDet = sel.costo > 0 ? (d.costo / sel.costo) * 100 : 0;
                  return (
                    <div key={d.ciclo} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
                      <span style={{ flex: 1, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {d.ciclo}
                      </span>
                      <span style={{ fontFamily: 'monospace', color: 'var(--ink-muted)', fontSize: '10px' }}>
                        {pctDet.toFixed(1)}%
                      </span>
                      <span style={{ fontFamily: 'monospace', color: color, fontWeight: 700, fontSize: '10px', minWidth: '70px', textAlign: 'right' }}>
                        {fmtCRC(d.costo)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ─── BarChart: costo/ha por lote ──────────────────────────────────────────────
// Barras horizontales, ordenadas descendente, top N lotes. Coloreo por severidad
// relativa al promedio del set: > 1.5× rojo, > 1× amarillo, < 0.5× verde, resto neutro.

function BarChartCostoHa({ filas, max = 15 }: { filas: FilaReporte[]; max?: number }) {
  const [activo, setActivo] = useState<string | null>(null);
  const [fijado, setFijado] = useState<string | null>(null);
  const detalleRef = useRef<HTMLDivElement>(null);
  if (filas.length === 0) return null;
  const top = [...filas]
    .filter(f => f.costo_por_ha > 0)
    .sort((a, b) => b.costo_por_ha - a.costo_por_ha)
    .slice(0, max);
  if (top.length === 0) return null;

  const maxVal = top[0].costo_por_ha;
  const promedio = top.reduce((s, f) => s + f.costo_por_ha, 0) / top.length;
  const loteActivo = fijado || activo;
  const filaActiva = loteActivo ? top.find(f => f.lote_num === loteActivo) : null;

  function colorBar(v: number): string {
    if (v > promedio * 1.5) return 'var(--tone-warn-text)';
    if (v > promedio)        return 'var(--emp-accent-txt)';
    if (v < promedio * 0.5)  return 'var(--tone-success-text)';
    return 'var(--tone-info-text)';
  }

  return (
    <div style={{
      padding: '14px 18px', borderRadius: '12px',
      background: 'var(--surface-raised)', border: '1px solid var(--line)',
      position: 'relative',   // ancla del tooltip flotante
      overflow: 'visible',    // permite que el tooltip salga del card
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <BarChart3 size={15} style={{ color: 'var(--emp-accent-txt)' }} />
          Top {top.length} lotes por costo/ha
        </div>
        <div style={{ display: 'flex', gap: '12px', fontSize: '10px', color: 'var(--ink-faint)' }}>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: 'var(--tone-success-text)', marginRight: 4 }} />Bajo</span>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: 'var(--tone-info-text)', marginRight: 4 }} />Normal</span>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: 'var(--emp-accent-txt)', marginRight: 4 }} />Alto</span>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: 'var(--tone-warn-text)', marginRight: 4 }} />Crítico</span>
          <span style={{ marginLeft: '4px' }}>Promedio: {fmtCRC(promedio)}</span>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {top.map(f => {
          const pct = (f.costo_por_ha / maxVal) * 100;
          const col = colorBar(f.costo_por_ha);
          const esActivo = loteActivo === f.lote_num;
          return (
            <div key={f.lote_num}
              onMouseEnter={() => !fijado && setActivo(f.lote_num)}
              onMouseLeave={() => !fijado && setActivo(null)}
              onClick={() => {
                setFijado(curr => {
                  const next = curr === f.lote_num ? null : f.lote_num;
                  if (next) scrollDetalle(detalleRef);
                  return next;
                });
              }}
              style={{
                display: 'grid', gridTemplateColumns: '70px 1fr 140px',
                alignItems: 'center', gap: '10px',
                padding: '2px 4px', margin: '-2px -4px',
                borderRadius: 4,
                cursor: 'pointer',
                background: esActivo ? 'var(--emp-accent-bg)' : 'transparent',
                outline: fijado === f.lote_num ? `1px solid ${col}66` : 'none',
                opacity: loteActivo == null || esActivo ? 1 : 0.55,
                transition: 'opacity 0.15s, background 0.15s',
              }}>
              <div style={{ fontSize: '12px', fontWeight: 700, fontFamily: 'monospace', color: 'var(--emp-accent-txt)', textAlign: 'right' }}>
                Lote {f.lote_num}
              </div>
              <div style={{ position: 'relative', height: '22px', background: 'var(--surface-deep)', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{
                  position: 'absolute', top: 0, left: 0, bottom: 0,
                  width: `${pct}%`, background: col,
                  transition: 'width 0.4s ease',
                  borderRadius: '4px',
                }} />
                {/* Línea de referencia del promedio */}
                <div style={{
                  position: 'absolute', top: -2, bottom: -2,
                  left: `${(promedio / maxVal) * 100}%`,
                  width: '2px', background: 'var(--ink-faint)',
                  borderRadius: '2px',
                }} title={`Promedio: ${fmtCRC(promedio)}`} />
              </div>
              <div style={{ fontSize: '12px', fontFamily: 'monospace', fontWeight: 700, color: col, textAlign: 'right' }}>
                {fmtCRC(f.costo_por_ha)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Tooltip: desglose por tipo de aplicación del lote activo.
          Posicionado FUERA del card (a la derecha) para no tapar las barras.
          Hacer click en un lote deja el panel fijo para lectura. */}
      {filaActiva && filaActiva.desglose_tipo.length > 0 && (() => {
        const total = filaActiva.costo_total;
        const colBarra = colorBar(filaActiva.costo_por_ha);
        return (
          <div style={{
            scrollMarginBlock: '18px',
            width: '100%', maxHeight: '300px', overflowY: 'auto',
            marginTop: '12px',
            padding: '12px 14px', borderRadius: '10px',
            background: 'var(--surface-overlay)',
            border: `1px solid ${colBarra}55`,
            boxShadow: '0 0 0 1px var(--line-dim)',
          }} ref={detalleRef}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', paddingBottom: '8px', borderBottom: '1px solid var(--line)' }}>
              <div style={{ width: 10, height: 10, borderRadius: 3, background: colBarra }} />
              <span style={{ fontSize: '13px', fontWeight: 800, color: 'var(--ink)' }}>
                Lote {filaActiva.lote_num}
              </span>
              <span style={{ fontSize: '10px', color: 'var(--ink-faint)', marginLeft: 'auto' }}>
                {fijado ? 'fijo' : filaActiva.finca}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '10px', color: 'var(--ink-faint)', marginBottom: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Costo total</span>
                <span style={{ fontFamily: 'monospace', color: 'var(--ink)', fontWeight: 700 }}>{fmtCRC(filaActiva.costo_total)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Costo/ha</span>
                <span style={{ fontFamily: 'monospace', color: colBarra, fontWeight: 700 }}>{fmtCRC(filaActiva.costo_por_ha)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Aplicaciones · Área</span>
                <span style={{ fontFamily: 'monospace', color: 'var(--ink-muted)' }}>{filaActiva.n_aplicaciones} · {filaActiva.area_total.toFixed(2)} ha</span>
              </div>
            </div>
            <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', color: 'var(--ink-faint)', marginBottom: '6px', letterSpacing: '0.05em' }}>
              Composición por tipo
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              {filaActiva.desglose_tipo.map(d => {
                const pctTipo = total > 0 ? (d.costo / total) * 100 : 0;
                const colorTipo = TIPO_CICLO_COLORS[d.tipo] || 'var(--ink-faint)';
                return (
                  <div key={d.tipo}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', marginBottom: '2px' }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: colorTipo, flexShrink: 0 }} />
                      <span style={{ flex: 1, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {d.tipo}
                      </span>
                      <span style={{ fontFamily: 'monospace', color: colorTipo, fontWeight: 700, fontSize: '10px' }}>
                        {pctTipo.toFixed(1)}%
                      </span>
                      <span style={{ fontFamily: 'monospace', color: 'var(--ink-muted)', fontSize: '10px', minWidth: '75px', textAlign: 'right' }}>
                        {fmtCRC(d.costo)}
                      </span>
                    </div>
                    <div style={{ height: '4px', background: 'var(--surface-deep)', borderRadius: 2, overflow: 'hidden', marginLeft: 14 }}>
                      <div style={{
                        height: '100%', width: `${pctTipo}%`,
                        background: colorTipo, borderRadius: 2,
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ─── Página ───────────────────────────────────────────────────────────────────

export function ReporteCostoLotePage() {
  const empresaId = useEmpresaId();

  const [fechaDesde, setFechaDesde] = useState('2025-01-01');
  const [fechaHasta, setFechaHasta] = useState(fechaHoy());
  const [loading, setLoading] = useState(false);
  const [filas, setFilas] = useState<FilaReporte[]>([]);
  const [porEstadio, setPorEstadio] = useState<EstadioReporte[]>([]);
  const [porCiclo, setPorCiclo] = useState<{ etiqueta: string; costo: number; detalle: { ciclo: string; costo: number }[] }[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>('costo_por_ha');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const cargar = useCallback(async () => {
    if (!fechaDesde || !fechaHasta) return;
    setLoading(true);

    // 1. Cabeceras VFP: solo aplicado=true en el rango.
    //    Supabase limita 1000 filas por query → paginar con range.
    const PAGE = 1000;
    const cabe: any[] = [];
    for (let offset = 0; ; offset += PAGE) {
      const { data, error: errCabe } = await supabase
        .from('app_vfp_cedula')
        .select('nboleta, lote_num, finca, area_ha, plantas, aplicado, faplica, estadio, nestadio, ciclo, idciclo')
        .eq('empresa_id', empresaId)
        .eq('aplicado', true)
        .gte('faplica', fechaDesde)
        .lte('faplica', fechaHasta)
        .order('faplica', { ascending: true })
        .range(offset, offset + PAGE - 1);
      if (errCabe) { setLoading(false); return; }
      const batch = data ?? [];
      cabe.push(...batch);
      if (batch.length < PAGE) break;        // última página
    }
    if (cabe.length === 0) {
      setFilas([]);
      setLoading(false);
      return;
    }

    const nboletas = cabe.map(c => c.nboleta);
    // 2. Detalles solo aplicado=true (suma de valor).
    //    Doble paginación: chunks de nboletas (evita URL gigante) +
    //    range interno (esquiva el límite de 1000 filas de Supabase).
    const valoresPorCedula = new Map<string, number>();
    const CHUNK = 200;     // ~200 cédulas → ~1500 detalles promedio
    for (let i = 0; i < nboletas.length; i += CHUNK) {
      const chunk = nboletas.slice(i, i + CHUNK);
      for (let offset = 0; ; offset += PAGE) {
        const { data: det, error: errDet } = await supabase
          .from('app_vfp_cedula_detalle')
          .select('nboleta, valor, aplicado')
          .eq('empresa_id', empresaId)
          .eq('aplicado', true)
          .in('nboleta', chunk)
          .range(offset, offset + PAGE - 1);
        if (errDet) { break; }
        const batch = (det ?? []) as { nboleta: string; valor: number | null }[];
        for (const d of batch) {
          valoresPorCedula.set(d.nboleta, (valoresPorCedula.get(d.nboleta) ?? 0) + Number(d.valor ?? 0));
        }
        if (batch.length < PAGE) break;     // última página de este chunk
      }
    }

    // 3. Agrupar por lote.
    //    Área y plantas: valores de REFERENCIA (cédula más reciente del lote),
    //                    no acumulados por cada aplicación.
    //    Aplicaciones y costo: SÍ son acumulados en el rango.
    // Acumulador por lote: área/plantas usan MODA (la más frecuente entre las
    // cédulas del lote), no "la más reciente". Razón: lotes con cédulas
    // parciales (sub-grupos de bloques) reportaban áreas pequeñas que inflaban
    // el costo/ha. La moda refleja mejor el área "completa" del lote.
    interface Acum {
      lote_num: string;
      finca: string;
      areas: Map<number, number>;       // area_ha → cantidad de cédulas que la usan
      plantas: Map<number, number>;     // plantas → cantidad de cédulas
      n_aplicaciones: number;
      costo_total: number;
    }
    const porLote = new Map<string, Acum>();
    // Desglose costo por (lote → tipo aplicación) para tooltip del BarChart
    const porLoteTipo = new Map<string, Map<string, number>>();
    for (const c of cabe as any[]) {
      // Normalizar lote_num al formato ERP (2 dígitos con padding).
      // VFP guarda '1', '2', '6' mientras ERP usa '01', '02', '06'.
      // Si es numérico de 1 dígito, paddear; si es 2+ dígitos o no-numérico, dejar.
      const loteRaw = String(c.lote_num ?? '').trim();
      const lote = (/^\d$/.test(loteRaw) ? '0' + loteRaw : loteRaw) || '—';
      const finca = (c.finca ?? '').trim() || '—';
      const area = Number(c.area_ha ?? 0);
      const plantas = Number(c.plantas ?? 0);
      const costo = valoresPorCedula.get(c.nboleta) ?? 0;
      // Acumular por tipo (clasificación agronómica del ciclo)
      const tipoApp = clasificarCiclo(String(c.ciclo ?? '').trim());
      let tipoMap = porLoteTipo.get(lote);
      if (!tipoMap) { tipoMap = new Map(); porLoteTipo.set(lote, tipoMap); }
      tipoMap.set(tipoApp, (tipoMap.get(tipoApp) ?? 0) + costo);
      let prev = porLote.get(lote);
      if (!prev) {
        prev = {
          lote_num: lote, finca,
          areas: new Map(), plantas: new Map(),
          n_aplicaciones: 0, costo_total: 0,
        };
        porLote.set(lote, prev);
      }
      prev.n_aplicaciones += 1;
      prev.costo_total += costo;
      if (!prev.finca || prev.finca === '—') prev.finca = finca;
      if (area > 0)    prev.areas.set(area,    (prev.areas.get(area)    ?? 0) + 1);
      if (plantas > 0) prev.plantas.set(plantas,(prev.plantas.get(plantas) ?? 0) + 1);
    }

    // Función auxiliar: devuelve la moda. En caso de empate, el valor MAYOR
    // (asumimos que el área "completa" es la grande, no el sub-grupo).
    function moda(m: Map<number, number>): number {
      let mejor = 0, mejorCount = 0;
      for (const [val, cnt] of m.entries()) {
        if (cnt > mejorCount || (cnt === mejorCount && val > mejor)) {
          mejor = val; mejorCount = cnt;
        }
      }
      return mejor;
    }

    const arr: FilaReporte[] = Array.from(porLote.values()).map(f => {
      const area_ref = moda(f.areas);
      const plantas_ref = moda(f.plantas);
      const tipoMap = porLoteTipo.get(f.lote_num);
      const desglose_tipo = tipoMap
        ? Array.from(tipoMap.entries())
            .map(([tipo, c]) => ({ tipo, costo: c }))
            .filter(d => d.costo > 0)
            .sort((a, b) => b.costo - a.costo)
        : [];
      return {
        lote_num: f.lote_num,
        finca: f.finca,
        area_total:    area_ref,
        plantas_total: plantas_ref,
        n_aplicaciones: f.n_aplicaciones,
        costo_total:   f.costo_total,
        costo_por_ha:     area_ref > 0    ? f.costo_total / area_ref    : 0,
        costo_por_planta: plantas_ref > 0 ? f.costo_total / plantas_ref : 0,
        desglose_tipo,
      };
    });
    setFilas(arr);

    // 4. Acumulado por estadio (todas las cédulas, no solo más recientes).
    //    Costo: suma de detalles. Área: suma del área de cada cédula que aportó costo.
    const mapaEstadio = new Map<string, { costo: number; area: number; n_aplicaciones: number; detalle: Map<string, number> }>();
    const ESTADIO_LABEL: Record<string, string> = {
      DPC: 'Desarrollo PC',
      FPC: 'Post Forza',
      DRC: 'Desarrollo RC',
      SRC: 'Semillero RC',
      SPC: 'Semillero PC',
      BAR: 'Barbecho',
    };
    for (const c of cabe as any[]) {
      const cod = String(c.estadio ?? '').trim().toUpperCase();
      const nombre = (c.nestadio && String(c.nestadio).trim())
        || ESTADIO_LABEL[cod]
        || (cod || 'Sin estadio');
      const costo = valoresPorCedula.get(c.nboleta) ?? 0;
      const area = Number(c.area_ha ?? 0);
      const ciclo = String(c.ciclo ?? '').trim() || '(sin nombre)';
      const prev = mapaEstadio.get(nombre) ?? { costo: 0, area: 0, n_aplicaciones: 0, detalle: new Map<string, number>() };
      prev.costo += costo;
      prev.area += area;
      prev.n_aplicaciones += 1;
      prev.detalle.set(ciclo, (prev.detalle.get(ciclo) ?? 0) + costo);
      mapaEstadio.set(nombre, prev);
    }
    setPorEstadio(
      Array.from(mapaEstadio.entries())
        .map(([estadio, v]) => ({
          estadio,
          costo: v.costo,
          area: v.area,
          n_aplicaciones: v.n_aplicaciones,
          detalle: Array.from(v.detalle.entries())
            .map(([ciclo, c]) => ({ ciclo, costo: c }))
            .filter(d => d.costo > 0)
            .sort((a, b) => b.costo - a.costo),
        }))
        .sort((a, b) => b.costo - a.costo)
    );

    // 5. Acumulado por TIPO de ciclo (categorización agronómica).
    //    Los 149 ciclos crudos se agrupan en 6 categorías por keywords.
    //    Además guardamos el desglose por ciclo individual para el tooltip.
    const mapaTipo = new Map<string, number>();
    const mapaDetalle = new Map<string, Map<string, number>>();
    for (const c of cabe as any[]) {
      const ciclo = String(c.ciclo ?? '').trim() || '(sin nombre)';
      const tipo = clasificarCiclo(ciclo);
      const costo = valoresPorCedula.get(c.nboleta) ?? 0;
      mapaTipo.set(tipo, (mapaTipo.get(tipo) ?? 0) + costo);
      let det = mapaDetalle.get(tipo);
      if (!det) { det = new Map(); mapaDetalle.set(tipo, det); }
      det.set(ciclo, (det.get(ciclo) ?? 0) + costo);
    }
    setPorCiclo(
      Array.from(mapaTipo.entries())
        .map(([etiqueta, costo]) => ({
          etiqueta,
          costo,
          detalle: Array.from((mapaDetalle.get(etiqueta) ?? new Map()).entries())
            .map(([ciclo, c]) => ({ ciclo, costo: c as number }))
            .filter(d => d.costo > 0)
            .sort((a, b) => b.costo - a.costo),
        }))
        .filter(c => c.costo > 0)
        .sort((a, b) => b.costo - a.costo)
    );

    setLoading(false);
  }, [empresaId, fechaDesde, fechaHasta]);

  useEffect(() => { cargar(); }, [cargar]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      const colNum = COLS.find(c => c.key === key)?.num ?? false;
      setSortDir(colNum ? 'desc' : 'asc');
    }
  }

  const filasOrdenadas = useMemo(() => {
    const copia = [...filas];
    copia.sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (typeof va === 'number' && typeof vb === 'number') {
        return sortDir === 'asc' ? va - vb : vb - va;
      }
      const sa = String(va ?? '');
      const sb = String(vb ?? '');
      // Para "lote_num" que es texto pero numérico, ordenar numéricamente cuando sea posible
      const na = Number(sa);
      const nb = Number(sb);
      if (Number.isFinite(na) && Number.isFinite(nb)) {
        return sortDir === 'asc' ? na - nb : nb - na;
      }
      return sortDir === 'asc' ? sa.localeCompare(sb, 'es') : sb.localeCompare(sa, 'es');
    });
    return copia;
  }, [filas, sortKey, sortDir]);

  // Totales / promedios
  const tot = useMemo(() => {
    const t = filas.reduce(
      (acc, f) => {
        acc.area += f.area_total;
        acc.plantas += f.plantas_total;
        acc.apl += f.n_aplicaciones;
        acc.costo += f.costo_total;
        return acc;
      },
      { area: 0, plantas: 0, apl: 0, costo: 0 },
    );
    return {
      ...t,
      costoPorHa:     t.area > 0    ? t.costo / t.area    : 0,
      costoPorPlanta: t.plantas > 0 ? t.costo / t.plantas : 0,
    };
  }, [filas]);

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="px-5 py-3 shrink-0 flex items-start gap-6 flex-wrap"
        style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)' }}>
        <div className="shrink-0 flex items-center gap-2">
          <BarChart3 size={20} style={{ color: 'var(--emp-accent-txt)' }} />
          <div>
            <h1 className={pageTitleCls}>Costo por hectárea por lote</h1>
            <p className={pageSubtitleCls}>Histórico VFP — solo cédulas aplicadas</p>
          </div>
        </div>

        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className={labelCls}>Del</label>
            <input type="date" className={inputCls} style={{ width: '10rem' }}
              value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Al</label>
            <input type="date" className={inputCls} style={{ width: '10rem' }}
              value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
          </div>
          <button onClick={cargar} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium"
            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            {loading ? 'Cargando…' : 'Consultar'}
          </button>
        </div>

        {/* Totales */}
        <div className="flex items-end gap-5 text-xs ml-auto">
          <Stat label="Lotes" value={fmtInt(filas.length)} />
          <Stat label="Área total (ha)" value={fmtN(tot.area, 2)} />
          <Stat label="Aplicaciones" value={fmtInt(tot.apl)} />
          <Stat label="Costo total" value={fmtCRC(tot.costo)} accent />
          <Stat label="Costo / ha" value={fmtCRC(tot.costoPorHa)} accent />
          <Stat label="Costo / planta" value={fmtCRC(tot.costoPorPlanta)} />
        </div>
      </div>

      {/* Body scrolleable: gráficos + tabla en el mismo scroll */}
      <div className="flex-1 overflow-auto">
      {/* Gráficos: barras (top costo/ha) + donut estadio + donut ciclo */}
      {!loading && filas.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
          gap: '14px',
          margin: '12px 16px 0',
        }}>
          <BarChartCostoHa filas={filas} />
          <DonutEstadio data={porEstadio} />
          <DonutCiclo data={porCiclo} />
        </div>
      )}

      {/* Hectáreas por fase de cosecha (inventario actual emp_bloques) */}
      <div style={{ margin: '14px 16px 0' }}>
        <BarsFaseCosecha empresaId={empresaId} />
      </div>

      {/* Tabla */}
      <div>
        {loading ? (
          <div className="flex items-center justify-center py-12 gap-2" style={{ color: 'var(--ink-faint)' }}>
            <Loader2 size={20} className="animate-spin" /> <span className="text-sm">Cargando reporte…</span>
          </div>
        ) : filas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 opacity-40">
            <BarChart3 size={36} style={{ color: 'var(--ink-faint)' }} />
            <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>
              Sin cédulas aplicadas en el rango seleccionado
            </p>
          </div>
        ) : (
          <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--surface-raised)' }}>
              <tr style={{ borderBottom: '1px solid var(--line)' }}>
                {COLS.map(col => {
                  const active = col.key === sortKey;
                  return (
                    <th key={col.key}
                      onClick={() => toggleSort(col.key)}
                      className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap select-none cursor-pointer"
                      style={{
                        textAlign: col.align ?? 'left',
                        color: active ? 'var(--emp-accent-txt)' : 'var(--ink-muted)',
                      }}>
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        <SortIcon active={active} dir={sortDir} />
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filasOrdenadas.map((f, i) => (
                <tr key={f.lote_num}
                  style={{
                    borderBottom: '1px solid var(--line-dim)',
                    background: i % 2 === 0 ? 'transparent' : 'var(--surface-deep)',
                  }}>
                  <td className="px-3 py-1.5 font-mono font-bold" style={{ color: 'var(--emp-accent-txt)' }}>
                    {f.lote_num}
                  </td>
                  <td className="px-3 py-1.5" style={{ color: 'var(--ink-muted)' }}>{f.finca}</td>
                  <td className="px-3 py-1.5 text-right font-mono" style={{ color: 'var(--ink)' }}>{fmtN(f.area_total, 2)}</td>
                  <td className="px-3 py-1.5 text-right font-mono" style={{ color: 'var(--ink)' }}>{fmtInt(f.plantas_total)}</td>
                  <td className="px-3 py-1.5 text-right font-mono" style={{ color: 'var(--ink-muted)' }}>{f.n_aplicaciones}</td>
                  <td className="px-3 py-1.5 text-right font-mono font-bold" style={{ color: 'var(--tone-success-text)' }}>{fmtCRC(f.costo_total)}</td>
                  <td className="px-3 py-1.5 text-right font-mono font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{fmtCRC(f.costo_por_ha)}</td>
                  <td className="px-3 py-1.5 text-right font-mono" style={{ color: 'var(--ink-muted)' }}>{fmtCRC(f.costo_por_planta)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot style={{ background: 'var(--surface-raised)', borderTop: '2px solid var(--line)' }}>
              <tr>
                <td colSpan={2} className="px-3 py-2 text-[11px] font-bold uppercase" style={{ color: 'var(--ink-muted)' }}>
                  Totales · {filas.length} lote{filas.length === 1 ? '' : 's'}
                </td>
                <td className="px-3 py-2 text-right font-mono font-bold" style={{ color: 'var(--ink)' }}>{fmtN(tot.area, 2)}</td>
                <td className="px-3 py-2 text-right font-mono font-bold" style={{ color: 'var(--ink)' }}>{fmtInt(tot.plantas)}</td>
                <td className="px-3 py-2 text-right font-mono font-bold" style={{ color: 'var(--ink)' }}>{fmtInt(tot.apl)}</td>
                <td className="px-3 py-2 text-right font-mono font-bold" style={{ color: 'var(--tone-success-text)' }}>{fmtCRC(tot.costo)}</td>
                <td className="px-3 py-2 text-right font-mono font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{fmtCRC(tot.costoPorHa)}</td>
                <td className="px-3 py-2 text-right font-mono font-bold" style={{ color: 'var(--ink-muted)' }}>{fmtCRC(tot.costoPorPlanta)}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-widest mb-0.5" style={{ color: 'var(--ink-faint)' }}>{label}</p>
      <p className="font-bold font-mono text-sm" style={{ color: accent ? 'var(--emp-accent-txt)' : 'var(--ink)' }}>{value}</p>
    </div>
  );
}

export default ReporteCostoLotePage;
